import { NextSeo } from 'next-seo';
import React from 'react';

import Footer from 'components/Footer';
import Menubar from 'components/Menubar';
import MobileTabBar from 'components/MobileTabBar';
import { useUserAuth } from 'contexts/UserAuth';
import Head from 'next/head';
import { useRouter } from 'next/router';
import 'react-loading-skeleton/dist/skeleton.css';
import { useWindowSize } from 'react-use';
import api from 'utils/api';
import redirectToLogin from 'utils/redirectToLogin';
import sessionStore from 'utils/sessionStore';
import { useScrollTo } from '../../utils/scrollTo';
const { useState, useEffect, useRef } = React;

const SunscreenResult = (props) => {
  const { seo } = props;

  const generateProductsSchemaGraph = () => {
    const products = dataSources.map((product) => {
      const priceMatch = product.price.match(/\d+/);
      const price = priceMatch ? priceMatch[0] : '0';

      return {
        '@type': 'Product',
        '@id': `https://www.sorra.net/cushion-review-2024#product-${product.id}`,
        name: product.product_name,
        image: [
          `https://www.sorra.net/images/2024/cushion-review/products/${product.id}.png`,
        ],
        description: `${product.brand_name} ${product.product_name} 的真實評價、價格比較、成份分析`,
        brand: {
          '@type': 'Brand',
          name: product.brand_name,
        },
        countryOfOrigin: product.origin,
        category: '粉底',
        offers: {
          '@type': 'Offer',
          price: price,
          priceCurrency: 'HKD',
          availability: 'https://schema.org/InStock',
          url: `https://www.sorra.net/cushion-review-2024#product-${product.id}`,
        },
      };
    });

    return {
      '@context': 'https://schema.org',
      '@graph': products,
    };
  };

  useEffect(() => {
    sessionStore.setReferralCode('cushionreview');
  }, []);

  return (
    <div className="bg-white">
      <NextSeo
        title={seo.title}
        description={seo.description}
        openGraph={{
          images: [
            {
              url: 'https://www.sorra.net/images/2024/cushion-review/mobile_banner.png',
              width: 800,
              height: 800,
              alt: 'Sorra',
            },
          ],
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateProductsSchemaGraph()),
          }}
        />
      </Head>
      <Menubar
        props={{
          homeExtend: false,
        }}
      />
      <div className={'globals-centerContentsMargin relative overflow-hidden'}>
        <div className="relative min-h-screen w-full">
          <Banner key="banner" />
        </div>
        <Footer />
        <MobileTabBar props={{}} />
      </div>
    </div>
  );
};

const Banner = () => {
  return (
    <div className="relative w-full">
      {/* 桌面版 */}
      <div className="relative aspect-[375/555] w-full lg:aspect-[1440/632]">
        {/* 手機版圖片 */}
        <img
          src="/images/2024/cushion-review/mobile_banner.png"
          className="block h-auto w-full object-contain lg:hidden"
          alt="乾／油肌1個月試用半霧光氣墊粉底6選實測！"
        />

        {/* 桌面版圖片 */}
        <img
          src="/images/2024/cushion-review/desktop_banner.png"
          className="hidden h-auto w-full object-contain lg:block"
          alt="乾／油肌1個月試用半霧光氣墊粉底6選實測！"
        />

        <div className="flex justify-center">
          <div className="relative -mt-6 w-full max-w-[900px]">
            <div className="border border-white/20 bg-white pt-3 backdrop-blur-sm lg:rounded-3xl lg:p-1">
              <h1 className="text-center text-[32px] font-bold leading-normal text-gray-800 lg:text-[40px]">
                乾／油肌1個月試用
                <br />
                半霧光氣墊粉底6選實測！
              </h1>
            </div>
            <div
              className="absolute -top-[60px] left-[10px] flex h-20 w-20 flex-col items-center justify-center rounded-full text-[15px] font-bold uppercase tracking-wide text-white lg:-top-[68px] lg:left-[40px] lg:h-28 lg:w-28 lg:text-[22px]"
              style={{
                backgroundColor: '#1E70FF',
              }}
            >
              <span>HOT</span>
              <span>TOPIC</span>
            </div>
          </div>
        </div>
      </div>

      {/* 描述區塊 */}
      <div className="mx-auto w-full max-w-[1440px] px-6 pb-6 lg:pb-8">
        <div className="rounded-2xl bg-white lg:px-20">
          <p className="text-center text-[16px] leading-relaxed text-gray-700 lg:text-[20px]">
            相信氣墊粉底是大家公認最方便的底妝產品～
            除了上妝快捷，亦便於攜帶，補妝時也十分方便。
          </p>
          <p className="text-center text-[16px] leading-relaxed text-gray-700 lg:text-[20px]">
            這次Sorra派出了2位分別是乾肌／油肌的編輯進行實測，精選了6款半霧光妝感的氣墊粉底，為大家帶來1個月的評測報告！
          </p>
          <p className="text-center text-[16px] leading-relaxed text-gray-700 lg:text-[20px]">
            涵蓋了專櫃、開架產品，比較保濕度、持久力、氧化度等等。到底乾／油肌會覺得哪牌更好用？一起來看看吧！
          </p>
        </div>
      </div>

      {/* 書籤區塊 (Round 1-6) */}
      <div className="mx-auto w-full max-w-[1440px] px-4 pb-8">
        <div className="mx-auto hidden max-w-[1200px] grid-cols-2 gap-x-6 gap-y-3 lg:grid lg:grid-cols-5">
          {/* 前 5 個 Bookmark */}
          <Bookmark
            title={'水油儀實測'}
            subtitle={'Round 1: Hydrating Test'}
            id={'round1'}
            seoTitle={'水油儀實測'}
          />

          <Bookmark
            title={'遮瑕度實測'}
            subtitle={'Round 2: Coverage Test'}
            id={'round2'}
            seoTitle={'遮瑕度實測'}
          />

          <Bookmark
            title={'10小時持久實測'}
            subtitle={'Round 3: Long-lasting Test'}
            id={'round3'}
            seoTitle={'10小時持久實測'}
          />

          <Bookmark
            title={'氧化/暗沉實測'}
            subtitle={'Round 4: Oxidation Test'}
            id={'round4'}
            seoTitle={'氧化/暗沉實測'}
          />

          <Bookmark
            title={'粉撲使用感'}
            subtitle={'Round 5: Puff Usage Experience'}
            id={'round5'}
            seoTitle={'粉撲使用感'}
          />

          {/* 空的佔位符 - 只在大螢幕顯示，小螢幕完全不存在 */}
          <div className="col-span-2" />

          <Bookmark
            title={'綜合實測結果'}
            subtitle={'Overall Review'}
            id={'round6'}
            seoTitle={'綜合實測結果'}
          />

          {/* 右側空的佔位符 - 只在大螢幕顯示 */}
          <div className="col-span-2" />
        </div>

        {/* 小螢幕時顯示的布局 */}
        <div className="grid grid-cols-2 place-items-center gap-x-2 gap-y-3 lg:hidden">
          <Bookmark
            title={'水油儀實測'}
            subtitle={'Round 1: Hydrating Test'}
            id={'round1'}
            seoTitle={'水油儀實測'}
          />
          <Bookmark
            title={'遮瑕度實測'}
            subtitle={'Round 2: Coverage Test'}
            id={'round2'}
            seoTitle={'遮瑕度實測'}
          />
          <Bookmark
            title={'10小時持久實測'}
            subtitle={'Round 3: Long-lasting Test'}
            id={'round3'}
            seoTitle={'10小時持久實測'}
          />
          <Bookmark
            title={'氧化/暗沉實測'}
            subtitle={'Round 4: Oxidation Test'}
            id={'round4'}
            seoTitle={'氧化/暗沉實測'}
          />
          <Bookmark
            title={'粉撲使用感'}
            subtitle={'Round 5: Puff Usage Experience'}
            id={'round5'}
            seoTitle={'粉撲使用感'}
          />
          <Bookmark
            title={'綜合實測結果'}
            subtitle={'Overall Review'}
            id={'round6'}
            seoTitle={'綜合實測結果'}
          />
        </div>
      </div>

      <ReviewContent key="review_content"> </ReviewContent>
    </div>
  );
};

const dataSources = [
  {
    id: 1,
    brand_name: 'CLIO',
    product_name: '水潤光彩透亮無瑕氣墊\n粉底',
    origin: '韓國',
    spf: 'SPF50+ PA++++',
    price: 'HK$310/15g×2',
    unit_price: '10.3',
    volume: '15g×2',
    refill_info: '補充裝: 約HK$82',
    shade_count: 3,
    shades: ['#F8DBCB', '#FADCBD', '#EBC19C'],
    label: '',
  },
  {
    id: 2,
    brand_name: 'TIRTIR',
    product_name: 'Mask Fit Cushion',
    origin: '韓國',
    spf: '\nSPF40 PA++',
    price: 'HK$158/18g',
    unit_price: '8.7',
    volume: '18g',
    refill_info: '沒有單售補充裝',
    shade_count: 40,
    shades: [
      '#F4E2D3',
      '#EED8C9',
      '#F2DFCA',
      '#EFE2CB',
      '#F1D1BF',
      '#F2CFB4',
      '#F2D1B3',
      '#F3D3B4',
      '#E6BCA2',
      '#E6BE99',
      '#E2C49C',
      '#E0AD91',
      '#E4B490',
      '#E1BF92',
      '#E0AC85',

      '#DDA87F',
      '#E3BF90',
      '#D6A37D',
      '#CD9A75',
      '#CE9D70',
      '#D6AB80',
      '#CE9670',
      '#CD9B6F',
      '#CB996B',
      '#CA9466',
      '#C8886A',
      '#C8946B',
      '#C49463',
      '#C48566',
      '#C68C67',

      '#C28D61',
      '#C58A5E',
      '#C27E62',
      '#B87C53',
      '#9C643F',
      '#693B24',
      '#6B4228',
      '#59311D',
      '#4D291A',
      '#35231B',
    ],
    label: '均價最低！\n最多色號！',
  },
  {
    id: 3,
    brand_name: 'WAKEMAKE',
    product_name: '持妝絲絨柔霧氣墊',
    origin: '韓國',
    spf: '\nSPF50+ PA+++',
    price: 'HK$210/15g',
    unit_price: '14',
    volume: '15g',
    refill_info: '沒有單售補充裝',
    shade_count: 3,
    shades: ['#F2D4B9', '#E9CCB2', '#E3C5A9'],
    label: '',
  },
  {
    id: 4,
    brand_name: 'Parnell',
    product_name: '積雪草精粹霧面氣墊\n（霧面）',
    origin: '韓國',
    spf: 'SPF50 PA++++',
    price: 'HK$370/15g×2',
    unit_price: '12.3',
    volume: '15g×2',
    refill_info: '沒有單售補充裝',
    shade_count: 3,
    shades: ['#F6CFA8', '#F4BF8F', '#E9B480'],
    label: '',
  },
  {
    id: 5,
    brand_name: 'Estée Lauder',
    product_name: '持久輕透柔霧氣墊粉底',
    origin: '韓國',
    spf: 'SPF25 PA+++',
    price: 'HK$420/12g×2',
    unit_price: '17.5',
    volume: '12g×2',
    refill_info: '補充裝: HK$300',
    shade_count: 5,
    shades: ['#DDA86C', '#DDAA84', '#DFA172', '#E7BB9E', '#DDAE81'],
    label: '',
  },
  {
    id: 6,
    brand_name: 'YSL',
    product_name: '昇級版輕透無重羽毛\n氣墊粉底',
    origin: '法國',
    spf: 'SPF23 PA++',
    price: 'HK$670/14g',
    unit_price: '47.8',
    volume: '14g',
    refill_info: '補充裝: HK$405',
    shade_count: 6,
    shades: ['#F9C8A4', '#FAC596', '#F9C49F', '#F6C398', '#F2B291', '#F3B88B'],
    label: '均價最高！',
  },
];

const skinTypeMap = {
  oily: { reviewer: 'Jessie', label: '油性肌', problem: '色素印' },
  natural: { reviewer: 'Katy', label: '中性肌', problem: '雀斑、泛紅' },
  dry: { reviewer: 'Manyi', label: '乾性肌', problem: '黑眼圈' },
};

const round1 = [
  {
    id: 6,
    desc: '上面後亦會有黏黏的感覺，亦會黏著頭髮，約30分鐘後感覺就會消失。',
    water: 42,
    water_count: '+6',
    oil: 48,
    oil_count: '-3',
    type: 'oily',
  },
  {
    id: 5,
    desc: '微霧面的光澤感有種媽生好皮的感覺。',
    water: 24,
    water_count: '-10',
    oil: 55,
    oil_count: '+3',
    type: 'dry',
  },
  {
    id: 3,
    desc: '一開始為霧面質地，上面很快就會乾，所以需要迅速拍開。面部出油會讓妝感看起來更加自然。',
    water: 29,
    water_count: '-5',
    oil: 53,
    oil_count: '+2',
    type: 'oily',
  },
  {
    id: 1,
    desc: '臉部的黏稠感很重，會有種「糊在一起」的不適感。',
    water: 33,
    water_count: '-1',
    oil: 52,
    oil_count: '+1',
    type: 'dry',
  },
  {
    id: 2,
    desc: '陶瓷肌妝感，上面後會有黏黏的感覺，亦會黏著頭髮，約30分鐘後感覺就會消失。',
    water: 36,
    water_count: '+2',
    oil: 51,
    oil_count: '-',
    type: 'oily',
  },
  {
    id: 4,
    desc: '剛上面是帶光澤的微霧面感，妝感相對自然。',
    water: 30,
    water_count: '-4',
    oil: 52,
    oil_count: '+2',
    type: 'dry',
  },
];

const round2 = [
  {
    id: 4,
    title: '遮瑕度最高之選:',
    desc: '6款中遮瑕最高！只要薄薄一層就感遮蓋大部分瑕疵，紅血絲、雀斑及暗瘡也能遮蓋，單用就能快速出門的好選擇～',
  },
  {
    id: 6,
    title: '最透薄之選:',
    desc: '與TIRTIR一樣有陶瓷肌妝感，但較為輕薄，因此面具感較弱。妝感是六款中最自然的，比較適合瑕疵較少的皮膚～',
  },
  {
    id: 1,
    title: '',
    desc: '僅次於Parnell，但顆粒很明顯，妝感較厚重⋯•',
  },
  {
    id: 2,
    title: '',
    desc: '遮瑕度高，但同時面具感較強！',
  },
  {
    id: 3,
    title: '',
    desc: '6成遮瑕力，各方面也很平均。',
  },
  {
    id: 5,
    title: '',
    desc: '5成遮瑕力同時保持透薄妝感，粉質很細緻。',
  },
];

const round3 = [
  {
    id: 5,
    title: '乾性肌之選：',
    desc: '',
    dry: {
      desc: '完妝感覺很貼膚，是唯一持妝8小時仍不覺得臉部有抽乾的感覺，而且微微出油後有光澤奶油肌的效果！鼻翼位有輕微脫妝很令人驚喜～',
      rating: 'A',
      extra: '10小時後鼻翼也只有少許因磨擦而造成的脫妝！',
    },
    oily: {
      desc: '持久力中等，整體遮瑕能維持到約50%，出油後妝感自然。',
      rating: 'B',
      extra: '',
    },
  },
  {
    id: 2,
    title: '油性肌之選：',
    desc: '',
    dry: {
      desc: '遮瑕力達8成以上，能感受到毛孔被瞬間填平。不過暗沉速度極快！帶妝1小時便暗沉，而且質地較厚重，焗臉感相當明顯！',
      rating: 'B-',
      extra: '下顎線位置在帶妝6小時亦開始浮現暗粒⋯',
    },
    oily: {
      desc: '持久力最高！約6小時後遮瑕力才開始下降，最終使用10小時，遮瑕力剩餘7成，相當驚喜。即使是容易因磨擦而脫妝的尾翼位置都能保持到50%的遮瑕力。',
      rating: 'A',
      extra: '唯眼皮位置有積線情況，需要用手指輕揉，再塗上碎粉或眼影。',
    },
  },
  {
    id: 4,
    title: '',
    desc: '',
    dry: {
      desc: '帶妝6小時後不見暗沉氧化，但持妝力一般，鼻翼脫妝面頰浮粉，法令紋位會乾裂積線⋯',
      rating: 'B-',
    },
    oily: {
      desc: '持久力一般。上妝約3小時後遮瑕力開始下降，摩擦的鼻翼脫妝嚴重，但整體不易暗沉氧化。',
      rating: 'B',
    },
  },
  {
    id: 6,
    title: '',
    desc: '',
    dry: {
      desc: '遮瑕力只有3成，帶妝6小時開始面色會發灰，而且法令紋位置會變得繃緊⋯',
      rating: 'B-',
    },
    oily: {
      desc: '持久度不俗！上妝10小時遮能維持約7成，與TIRTIR相比，鼻翼脫妝稍為較多，因此小扣分。',
      rating: 'A-',
    },
  },
  {
    id: 3,
    title: '',
    desc: '',
    dry: {
      desc: '帶妝4小時開始脫妝，臉部會繃緊起屑！遮瑕膏會連帶起結塊，法令紋位置會積線⋯',
      rating: 'C',
    },
    oily: {
      desc: '約3小時後遮瑕力開始下降，但仍能保持完整。出油會讓妝感看起來更自然，亦未見暗沉氧化情況。',
      rating: 'B+',
    },
  },
  {
    id: 1,
    title: '',
    desc: '',
    dry: {
      desc: '帶妝6小時除了鼻翼脫妝外，整體妝容完整亦不見暗沉。不過下顎位會浮粉，令妝容看起來很粗糙！',
      rating: 'B+',
    },
    oily: {
      desc: '持久力低。約3小時後遮瑕力已開始下降至60%，面部不平整之凹凸位置浮現，亦有暗沉問題。',
      rating: 'C',
    },
  },
];

const round4 = [
  {
    id: 2,
    desc: '6款中氧化最嚴重！甚至由Cool Tone變成Warm Tone⋯有其實編輯反映Warm Tone的色號會變得更暗黃，大家選色時要小心！',
  },
  {
    id: 5,
    desc: '30分鐘後只有輕微變黃，並沒有特別變暗，是6款中色差最粉的一款！這種情況在一般霧面粉底為常見，完全可以接受～',
  },
  {
    id: 6,
    desc: '暗沉約0.5度',
  },
  {
    id: 1,
    desc: '暗沉約1度',
  },
  {
    id: 4,
    desc: '暗沉約1度',
  },
  {
    id: 3,
    desc: '暗沉約1.5度',
  },
];

const round5 = [
  {
    id: 4,
    desc: '粉撲使用感不好，比較薄而且難取粉。',
  },
  {
    id: 3,
    desc: '粉撲軟硬適中，取粉頗均勻，表現不錯！',
  },
  {
    id: 5,
    desc: '勉強能塞入3指，每次取粉量較少，需要更多時間上妝。',
  },
  {
    id: 1,
    desc: '有水滴型尖頭設計，粉撲也很柔軟，不過容易印出粉痕。',
  },
  {
    id: 2,
    desc: '橢圓形粉撲出奇地好用，容易發力，而且範圍亦較大。',
  },
  {
    id: 6,
    desc: '勉強能塞入3指，雖然有尖角設計，但上粉範圍較少！',
  },
];

const round6 = [
  {
    id: 1,
    spf: 'SPF50+ PA++++',
    overall_experience:
      '整體表現平均，唯獨是粉質較不細緻，疊加同時會令妝感變重、面具感較重⋯•',
    rating: 'C+',
    label: [
      {
        key: 'spf',
        desc: '防曬最強!',
      },
    ],
  },
  {
    id: 2,
    spf: 'SPF40 PA++',
    overall_experience:
      '6款中色號選擇最多、遮瑕度偏高，同時氧化問題最嚴重。在油肌上表現不錯，但乾肌則有點災難，評價兩極，但普遍認同妝感較厚重⋯•',
    rating: 'B-',
    label: [
      {
        key: 'product',
        desc: '油肌PICKS!',
      },
      {
        key: 'price',
        desc: '均價最低!',
      },
    ],
  },
  {
    id: 3,
    spf: 'SPF50+ PA+++',
    overall_experience:
      '中等遮瑕度，但氧化問題較嚴重，同時持久度也一般，整體表現不過不失。',
    rating: 'C',
    label: [],
  },
  {
    id: 4,
    spf: 'SPF50+ PA++++',
    overall_experience:
      '6款中遮瑕度最高，同時妝感細緻不會厚重！有氧化問題但仍算可接受範圍，持久度一般，但在乾肌上會鞍乾，比較適合偏油肌~',
    rating: 'B',
    label: [
      {
        key: 'spf',
        desc: '防曬最強!',
      },
      {
        key: 'description',
        desc: '遮瑕最強!',
      },
    ],
  },
  {
    id: 5,
    spf: 'SPF25 PA+++',
    overall_experience:
      '有中等遮瑕度，在2款膚質上持久度都不錯！乾肌上亦不會乾，平均表現最高分，而且價錢在專櫃中也不算貴，值得一試！~',
    rating: 'A',
    label: [
      {
        key: 'product',
        desc: '乾肌PICKS!',
      },
      {
        key: 'description',
        desc: '最持久!',
      },
    ],
  },
  {
    id: 6,
    spf: 'SPF34 PA++',
    overall_experience:
      '6款中最透薄、自然；同時遮瑕度最低。在油肌上持久度不錯！但粉樸尺寸較細，使用時較不就手...~',
    rating: 'B+',
    label: [
      {
        key: 'price',
        desc: '均價最高!',
      },
      {
        key: 'description',
        desc: '最透薄!',
      },
    ],
  },
];

const Bookmark = ({ title, subtitle, id, seoTitle, className }) => {
  const scrollTo = useScrollTo();
  const handleScroll = (id) => {
    if (typeof window !== 'undefined') {
      scrollTo(id);
    }
  };
  return (
    <div
      className={`${className} w-full max-w-[180px] cursor-pointer rounded-full border-2 bg-white px-1 py-1.5 text-center transition-colors hover:bg-blue-50 lg:max-w-[220px] lg:px-2 lg:py-3`}
      style={{ borderColor: '#1E70FF', boxShadow: '4px 4px 0px #1E70FF' }}
      onClick={() => handleScroll(id)}
    >
      <div
        className="text-[15px] font-bold md:text-[18px] lg:text-[20px]"
        style={{ color: '#1E70FF' }}
      >
        {title}
      </div>
      <h2 className="hidden">{seoTitle}</h2>
      <div
        className="text-[8px] font-bold md:text-[11px] lg:text-[12px]"
        style={{ color: '#1E70FF' }}
      >
        {subtitle}
      </div>
    </div>
  );
};

const ProductCard = ({ product = {} }) => {
  return (
    <div className="flex h-[334px] w-[182px] flex-col justify-start rounded-xl border-b bg-white px-2 pb-4 pt-4 shadow-md lg:h-[368px] lg:w-[280px] lg:border-b lg:py-2 lg:shadow-md">
      {/* 圖片容器 - 固定高度並底部對齊 */}
      <div className="relative h-[210px] w-full lg:h-[160px] lg:pt-4">
        {product.label && (
          <div className="absolute -right-2 -top-8 z-10">
            <div className="flex rotate-12 transform items-center">
              {/* 左斜線 SVG */}
              <svg width="20" height="40" className="text-[#1E70FF]">
                <line
                  x1="5"
                  y1={`${product.label.length > 5 ? 8 : 16}`}
                  x2="15"
                  y2="32"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>

              {/* 文字 */}
              <div className="mx-1 text-center">
                <div className="whitespace-nowrap text-[14px] font-bold text-[#1E70FF] lg:text-[20px]">
                  {product.label.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>

              {/* 右斜線 SVG */}
              <svg width="20" height="40" className="text-[#1E70FF]">
                <line
                  x1="15"
                  y1={`${product.label.length > 5 ? 8 : 16}`}
                  x2="5"
                  y2="32"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        )}
        <div className="flex h-[120px] items-end justify-center lg:h-[128px]">
          <img
            src={getProductImageByID(product?.id)}
            alt={`${product.brand_name} ${product.product_name}`}
            className="h-full w-full object-contain object-bottom"
          />
        </div>
        <div
          className="absolute bottom-0 right-[5px] flex h-[35px] w-[50px] flex-col items-center justify-center -space-y-1 rounded-[50%] tracking-wide text-white lg:-bottom-6 lg:right-[20px] lg:h-[60px] lg:w-[85px] lg:-space-y-1.5"
          style={{
            backgroundColor: '#1E70FF',
          }}
        >
          <span className="text-[8px] lg:text-[11px]">每ml均價</span>
          <span className="whitespace-nowrap text-[14px] font-bold lg:text-[24px]">
            <span className="text-[8px] font-bold lg:text-[12px]">$</span>
            {product.unit_price}
          </span>
        </div>
      </div>
      {/* 品牌標籤 */}
      <div className="text-center lg:pb-2 lg:pt-4">
        <div className="inline-flex px-2 text-[16px] font-semibold lg:text-[24px]">
          {product.brand_name}
        </div>
        {/* 產品名稱 */}
        <div className="hidden text-center text-[14px] leading-tight lg:text-[16px]">
          {product.product_name}
        </div>
        {/* spf */}
        <div className="hidden text-center text-[14px] leading-tight lg:text-[16px]">
          {product.spf}
        </div>
        <div className="whitespace-pre-line text-center text-[14px] leading-tight lg:hidden lg:text-[16px]">
          {product.product_name} {product.spf}
        </div>
      </div>
      <h3 className="hidden">
        {product.brand_name} {product.product_name}
      </h3>

      <div className="inline-flex pt-2 text-left lg:mx-6 lg:pb-2">
        <div
          className="inline-flex self-start rounded-full px-2 py-0.5 text-[12px] text-white lg:px-4 lg:text-[14px]"
          style={{ backgroundColor: '#1E70FF' }}
        >
          價錢
        </div>
        {/* 產地 */}
        <div className="ml-2 text-left text-[12px] lg:text-[16px]">
          {product.price}
          <br />
          {product.refill_info}
        </div>
      </div>
      <div className="flex items-start pb-4 pt-2 text-left lg:mx-6">
        <div
          className="flex-shrink-0 rounded-full px-2 py-0.5 text-[12px] text-white lg:px-4 lg:text-[14px]"
          style={{ backgroundColor: '#1E70FF' }}
        >
          色號
        </div>
        <div className="ml-2 flex-1 whitespace-nowrap text-[12px] lg:text-[16px]">
          {' '}
          <span className="font-bold">{product.shade_count}</span>個選擇
          {renderShades(product.shades)}
        </div>
      </div>
    </div>
  );
};

const renderShades = (shades) => {
  const { width } = useWindowSize();

  if (!shades || shades.length === 0) return null;

  if (shades.length <= 10) {
    // 不足10個，分散排列
    return (
      <div
        className={
          'flex items-center justify-between ' +
          (shades.length <= 3
            ? 'w-2/5 lg:w-1/2'
            : shades.length <= 6
              ? 'w-5/6'
              : 'w-full')
        }
      >
        {shades.map((shade, index) => (
          <div
            key={index}
            className="inline-flex h-[14px] w-[14px] rounded-full lg:h-4 lg:w-4"
            style={{ backgroundColor: shade }}
          />
        ))}
      </div>
    );
  } else {
    let oneLine = width < 1024 ? 15 : 20;

    const rows = [];
    for (let i = 0; i < shades.length; i += oneLine) {
      rows.push(shades.slice(i, i + oneLine));
    }

    return (
      <div className="-space-y-1 lg:-space-y-1.5">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex -space-x-0.5 lg:-space-x-1">
            {row.map((shade, index) => (
              <div
                key={index}
                className="inline-flex h-[10px] w-[10px] rounded-full lg:h-3 lg:w-3"
                style={{
                  backgroundColor: shade,
                  zIndex: index, // 重疊時的層級
                }}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }
};

const ReviewContent = () => {
  const { user } = useUserAuth();
  const router = useRouter();
  const [selectedFactor, setSelectedFactor] = useState('dry');
  const [selectedDryFirstFactor, setSelectedDryFirstFactor] = useState('dry');
  const [selectedOilyFirstFactor, setSelectedOilyFirstFactor] = useState('dry');
  return (
    <div key="review_content">
      <div style={{ backgroundColor: '#FEFBFD' }}>
        <div className="mx-auto w-full max-w-[1440px] px-2 pt-6">
          {/* 主標題 */}
          <h2
            className="mb-2 text-center text-[24px] font-bold lg:mb-6 lg:text-[40px]"
            style={{ color: '#1E70FF' }}
          >
            實測選手檔案
          </h2>

          {/* 描述文字 */}
          <div className="mb-2 px-4 py-2 lg:mb-8 lg:px-20">
            <p className="text-center text-[16px] text-base leading-relaxed text-gray-700 lg:text-[20px]">
              小編們精選了開架／專櫃中最具人氣的6款氣墊粉底，全部均為半霧面妝感～
              <br />
              其中以TIRTIR的均價為最低，同時色號也有最多選擇！另外專櫃代表的YSL則為均價最高。{' '}
              <br />
              當中有部分不能單售補充裝，回購時需重新購買外包裝，較不環保⋯
            </p>
          </div>

          {/* 產品卡片區塊 */}
          <div className="flex flex-col lg:flex-row lg:gap-3">
            <div className="w-full lg:pb-14 lg:pt-14">
              <div className="hidden lg:block">
                {/* 第一列：3個商品，置中 */}
                <div className="flex justify-center gap-x-4 pb-4 pt-4">
                  {dataSources.slice(0, 3).map((product, index) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* 第二列：3個商品，置中 */}
                <div className="flex justify-center gap-x-4 pb-4 pt-4">
                  {dataSources.slice(3, 6).map((product, index) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 產品卡片區塊 */}
          <div
            className="block lg:hidden"
            style={{ backgroundColor: '#FAFAFA' }}
          >
            <div className="overflow-x-auto pb-8 pt-4">
              <div
                className="ml-4 grid gap-4"
                style={{
                  gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  width: 'max-content',
                  minWidth: '100%',
                }}
              >
                {dataSources.slice(0, 6).map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    style={{
                      gridColumn: Math.floor(index / 2) + 1,
                      gridRow: index % 2,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Round1 區塊 */}
      <section id="round1"></section>
      <div style={{ backgroundColor: '#FAFAFA' }}>
        <div className="mx-auto w-full max-w-[1440px] px-2 pt-4">
          <div className="mx-auto max-w-7xl lg:px-6 lg:pt-6">
            <div
              className="mt-4 pb-8 text-center text-[24px] font-bold lg:text-[32px]"
              style={{ color: '#1E70FF' }}
            >
              Round 1：水油儀保濕度測試
            </div>
            <div className="flex w-full flex-col items-center justify-center lg:hidden">
              <p className="text-medium px-6 pb-4 text-center text-[16px] font-normal leading-relaxed lg:pb-8 lg:text-[20px]">
                編輯採用水油儀測量上妝前及上妝後分別的數據，比較皮膚表面的含水量／含油量～
                若含水量上升，則代表有較好的即時保濕度。若含水量大幅下降，則代表粉體較乾身，持久度可能會更好～
              </p>
              <div className="flex items-center space-x-4 px-7">
                <span className="whitespace-nowrap rounded-3xl bg-[#1E70FF] px-1.5 text-[14px] font-bold text-white">
                  上妝前
                </span>
                <span className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-black text-[16px] text-white">
                  水
                </span>
                <p className="font-rubik text-[32px] font-bold">34</p>
                <span className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-black text-[16px] text-white">
                  油
                </span>
                <p className="font-rubik text-[32px] font-bold">51</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="maskItem"
        className={
          'bg-red relative z-50 h-0 w-full' + (user ? ' hidden' : ' block')
        }
      >
        <div className="absolute bottom-0 left-0 flex w-full items-center justify-center">
          <div className="relative min-h-[400px] w-full items-center justify-center bg-gradient-to-t from-[#FFFFFF] from-40% via-[#FFFFFF]/90 via-80% to-transparent px-6 py-2 text-center text-black shadow-2xl lg:min-h-[340px]">
            <img
              src="/images/block.png"
              className="inline-flex h-[163px] w-[216px] pb-2"
            />
            <div className="mb-2 text-[16px] font-semibold lg:text-[20px]">
              立即成為 <span className="font-extrabold">Sorra</span>{' '}
              會員繼續查看！
            </div>
            <p className="mb-4 text-[12px] lg:text-[16px]">
              會員可解鎖所有評測內容
            </p>
            <button
              className="inline-flex h-[41px] w-[151px] items-center justify-center gap-2 rounded-md bg-[#EC6C74] px-4 py-2 text-sm text-[#FFFFFF] shadow-sm hover:bg-[#FCE1E5]"
              onClick={() => {
                redirectToLogin(router.asPath);
              }}
            >
              <div className="h-[20px] w-[20px]">
                <img
                  src="/svgs/user-w.svg"
                  className="h-full w-full object-cover"
                />
              </div>
              免費成為會員
            </button>
          </div>
        </div>
      </div>

      {user && (
        <div>
          <div style={{ backgroundColor: '#FAFAFA' }}>
            <div className="mx-auto w-full max-w-[1440px] px-2 pb-4">
              <div className="mx-auto max-w-7xl lg:px-6 lg:pb-6">
                <div className="flex w-full flex-col items-center justify-center lg:hidden">
                  <img
                    src="/images/2024/cushion-review/round1/1.png"
                    alt="水油儀"
                    className="h-[261px] h-full w-[327px] w-full object-contain pt-2"
                  />
                </div>
                {/* Round 1 桌面 */}
                <div className="hidden h-[435px] w-full flex-row items-center justify-center lg:flex">
                  <div className="flex-1 flex-col py-2 pr-8">
                    <p className="text-medium text-left font-normal leading-relaxed lg:pb-8 lg:text-[20px]">
                      編輯採用水油儀測量上妝前及上妝後分別的數據，比較皮膚表面的含水量／含油量～
                      若含水量上升，則代表有較好的即時保濕度。若含水量大幅下降，則代表粉體較乾身，持久度可能會更好～
                    </p>
                    <div className="flex items-center space-x-6">
                      <span className="whitespace-nowrap rounded-3xl bg-[#1E70FF] px-4 py-2 text-[16px] font-bold text-white">
                        上妝前
                      </span>
                      <span className="rounded-3xl bg-black px-3 py-2 text-[20px] text-white">
                        水
                      </span>
                      <p className="font-rubik text-[40px] font-bold">34</p>
                      <span className="rounded-3xl bg-black px-3 py-2 text-[20px] text-white">
                        油
                      </span>
                      <p className="font-rubik text-[40px] font-bold">51</p>
                    </div>
                  </div>
                  <div className="flex">
                    <img
                      src="/images/2024/cushion-review/round1/1.png"
                      alt="水油儀"
                      className="h-[435px] h-full w-[544px] w-full rounded-2xl object-contain"
                    />
                  </div>
                </div>

                <div className="mx-auto mt-8 lg:grid lg:grid-cols-2 lg:gap-6">
                  <div className="mb-4 flex flex-col items-center lg:mb-0">
                    <p className="pb-4 text-center text-[20px] font-bold text-[#1E70FF] lg:pb-8 lg:text-[24px]">
                      最保濕之選：YSL
                    </p>
                    <div className="flex-1">
                      {' '}
                      {/* 讓卡片佔滿剩餘空間 */}
                      <MoistureOilBar
                        data={round1[0]}
                        index={1}
                        dataSources={dataSources}
                        className="h-full" // 確保卡片填滿高度
                      />
                    </div>
                  </div>

                  <div className="flex flex-col items-center">
                    <p className="pb-4 pt-2 text-center text-[20px] font-bold text-[#1E70FF] lg:pb-8 lg:text-[24px]">
                      最控油之選：Estée Lauder
                    </p>
                    <div className="flex-1">
                      <MoistureOilBar
                        data={round1[1]}
                        index={2}
                        dataSources={dataSources}
                        className="h-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-auto w-full max-w-[1440px] px-2">
                <p className="pt-6 text-center text-[20px] font-bold leading-relaxed text-[#1E70FF] lg:pb-4 lg:pt-10 lg:text-[24px]">
                  其它結果
                </p>
                <div className="hidden flex-row items-center justify-center space-x-4 lg:flex">
                  {round1.slice(2, 6).map((result, index) => (
                    <MinMoistureOilBar
                      data={result}
                      index={index + 3}
                      dataSources={dataSources}
                    />
                  ))}
                </div>

                <div className="scrollbar-hide -mx-4 flex h-full gap-0 overflow-x-auto pb-4 pt-4 lg:hidden">
                  {round1.slice(2, 6).map((result, index) => (
                    <div className="border-gray h-full flex-shrink-0 border-r px-4">
                      <div
                        className="h-[340px]"
                        style={{ backgroundColor: '#FEFBFD' }}
                      >
                        <MinMoistureOilBar
                          data={result}
                          index={index + 3}
                          dataSources={dataSources}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Round2 區塊 */}
          <section id="round2"></section>
          <div className="mx-auto w-full max-w-[1440px] px-2 py-4">
            <div
              className="text-center text-[24px] font-bold lg:mb-4 lg:text-[32px]"
              style={{ color: '#1E70FF' }}
            >
              Round 2：遮瑕度
            </div>
            <div className="mb-4 px-4 py-2 lg:mb-8">
              <p className="text-center text-[16px] text-base leading-relaxed text-gray-700 lg:text-[20px]">
                利用眼線筆實測粉底遮蓋黑色素的能力～
              </p>
            </div>
            {/* 桌面版佈局 */}
            <div className="mx-auto hidden max-w-[1224px] items-center lg:block">
              <div className="grid grid-cols-4 lg:grid-cols-4 lg:items-center lg:gap-4 lg:py-2">
                <div className="lg:w-[275px]] text-center">
                  <div className="justify-center">
                    <p className="my-2 text-[20px] font-bold text-[#2070E5] lg:text-[24px]">
                      遮瑕度最高之選:
                      <br />
                      Parnell
                    </p>
                    <p className="text-[16px] font-light font-normal lg:text-[20px]">
                      {round2[0].desc}
                    </p>
                  </div>
                </div>
                <div className="item-center w-[220px] pt-4 text-center lg:w-[275px]">
                  <Round2Card
                    key={round2[0].id}
                    id={round2[0].id}
                    index={1}
                    round={2}
                    isEnlarge={true}
                    isMove={false}
                  />
                </div>

                <div className="justify-end text-center lg:w-[275px]">
                  <div className="justify-end rounded-lg">
                    <p className="my-2 text-[20px] font-bold text-[#2070E5] lg:text-[24px]">
                      最透薄之選:
                      <br />
                      YSL
                    </p>
                    <p className="text-[16px] font-light font-normal lg:text-[20px]">
                      {round2[1].desc}
                    </p>
                  </div>
                </div>
                <div className="item-center w-[220px] pt-4 text-center lg:w-[275px]">
                  <Round2Card
                    key={round2[1].id}
                    id={round2[1].id}
                    index={2}
                    round={2}
                    isEnlarge={true}
                    isMove={false}
                  />
                </div>
              </div>

              {/* 桌面版網格佈局 */}
              <div className="grid grid-cols-4 gap-x-4 gap-y-8">
                {round2.slice(2, 6).map((result, index) => (
                  <div className="pt-8">
                    <div className="item-center w-[275px] pt-4 text-center">
                      <Round2Card
                        key={round2[index + 2].id}
                        id={round2[index + 2].id}
                        index={index + 3}
                        round={2}
                        isEnlarge={true}
                        isMove={false}
                      />
                    </div>
                    <div className="mx-4 w-[260px] items-center text-center lg:pt-8">
                      <p className="text-[20px] font-normal">{result.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 手機版佈局 */}
            <div className="block lg:hidden">
              <div className="flex flex-col items-center gap-1 px-4 lg:py-2">
                <div className="justify-end text-center">
                  <div className="justify-end rounded-lg">
                    <p className="mb-6 text-[20px] font-bold text-[#2070E5] lg:text-[32px]">
                      遮瑕度最高之選:
                      <br />
                      Parnell
                    </p>
                  </div>
                </div>
                <div className="item-center w-[220px] text-center lg:w-[275px]">
                  <Round2MobileCard
                    key={round2[0].id}
                    id={round2[0].id}
                    index={1}
                    round={2}
                    isEnlarge={true}
                    isMove={false}
                  />
                </div>
                <p className="mt-4 text-center text-[16px] font-light">
                  {round2[0].desc}
                </p>

                <div className="justify-end text-center">
                  <div className="justify-end rounded-lg">
                    <p className="mb-6 mt-6 text-[20px] font-bold text-[#2070E5] lg:text-[32px]">
                      最透薄之選:
                      <br />
                      YSL
                    </p>
                  </div>
                </div>
                <div className="item-center w-[220px] text-center lg:w-[275px]">
                  <Round2MobileCard
                    key={round2[1].id}
                    id={round2[1].id}
                    index={2}
                    round={2}
                    isEnlarge={true}
                    isMove={false}
                  />
                </div>
                <p className="mt-4 text-center text-[16px] font-light">
                  {round2[1].desc}
                </p>
              </div>
              <div className="justify-end text-center">
                <p className="mb-4 mt-8 text-[20px] font-bold text-[#2070E5]">
                  其它結果
                </p>
              </div>
              <div className="scrollbar-hide -mx-4 flex h-full gap-4 overflow-x-auto px-6 pb-4 pt-4">
                {round2.slice(2, 6).map((result, index) => (
                  <div className="h-full flex-shrink-0">
                    <div className="h-[220px] text-center">
                      <Round2MobileCard
                        key={result.id}
                        id={result.id}
                        index={index + 3}
                        desc={result.desc}
                        round={2}
                        isEnlarge={false}
                        isMove={false}
                      />
                    </div>
                    <div className="w-[148px] items-center pb-4 text-center">
                      <p className="text-[16px] font-normal">{result.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Round3 區塊 */}
          <section id="round3"></section>
          <div style={{ backgroundColor: '#FAFAFA' }}>
            <div className="mx-auto w-full max-w-[1440px] pt-8 lg:px-2 lg:py-4">
              <div
                className="mb-4 text-center text-[24px] font-bold lg:mb-8 lg:pt-12 lg:text-[32px]"
                style={{ color: '#1E70FF' }}
              >
                Round 3：持久度
              </div>

              <div className="mb-4 px-4 py-2 md:px-20">
                <p className="text-center text-[16px] text-base leading-relaxed text-gray-700 lg:text-[20px]">
                  以2位不同膚質的小編上面實測，每款粉底測試8-10小時。
                </p>
              </div>

              <div className="hidden lg:block">
                <div className="mx-auto max-w-[1086px] rounded-xl border border-gray-100 bg-white pb-2 pt-6 shadow-md lg:px-8 lg:pb-5 lg:pt-4">
                  {/* 桌面版 */}
                  <div className="">
                    <div className="px-4 py-2 md:px-20">
                      <p
                        className="text-center text-[24px] font-extrabold lg:text-[32px]"
                        style={{ color: '#2070E5' }}
                      >
                        乾性肌之選：
                        <span className="font-bold">Estée Lauder</span>
                      </p>
                    </div>
                    {/* 產品標題區 */}
                    <div className="mb-6 mt-6 flex items-center justify-center">
                      <img
                        src={getProductImageByID2(round3[0].id)}
                        alt={dataSources[round3[0].id - 1].product_name}
                        className="h-23 mr-4 w-20 object-contain"
                      />
                      <div className="text-left">
                        <p className="text-[32px] font-bold">
                          {dataSources[round3[0].id - 1].brand_name}
                        </p>
                        <p className="text-[20px] text-gray-600">
                          {dataSources[round3[0].id - 1].product_name}
                        </p>
                      </div>
                    </div>

                    {/* 兩欄評測區域 */}
                    <div className="flex">
                      {/* 左側評測 */}
                      <div className="flex-1 pr-4">
                        <div className="mb-4 flex items-center">
                          <p className="mr-3 font-rubik text-[32px] font-bold text-[#2070E5]">
                            Manyi
                          </p>
                          <span className="rounded-full bg-[#2070E5] px-3 py-1 text-white lg:text-[16px]">
                            乾性肌
                          </span>
                        </div>

                        {/* 對比圖片 */}
                        <div className="relative mb-4">
                          <div className="grid grid-cols-2 gap-0">
                            <div className="relative h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/1-dry-before.png"
                                alt="使用前"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                剛上妝
                              </div>
                            </div>

                            <div className="relative -mx-1 h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/1-dry-after.png"
                                alt="使用後"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                10小時後
                              </div>
                            </div>
                          </div>

                          {/* 評分徽章 */}
                          <div
                            className="absolute -bottom-4 -right-4 z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#2070E5] text-[40px] font-bold text-white shadow-lg"
                            style={{ boxShadow: '0 0 0 4px white' }}
                          >
                            {round3[0].dry.rating}
                          </div>
                        </div>

                        <p className="mb-4 text-[20px] leading-relaxed text-gray-700">
                          {round3[0].dry.desc}
                        </p>

                        {/* 額外圖片 */}
                        {round3[0].dry.extra && (
                          <div className="grid grid-cols-2 gap-0">
                            <img
                              src="/images/2024/cushion-review/round3/1-dry-extra.png"
                              alt="細節圖"
                              className="mb-2 h-[170px] w-[240px] object-cover"
                            />
                            <div className="flex items-center justify-center">
                              <p className="item-center mx-4 justify-center text-[20px] text-gray-600">
                                {round3[0].dry.extra}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 中間分隔線 */}
                      <div className="mx-4 w-px bg-gray-100"></div>

                      {/* 右側評測 */}
                      <div className="flex-1 pl-4">
                        <div className="mb-4 flex items-center">
                          <p className="mr-3 font-rubik text-[32px] font-bold text-[#2070E5]">
                            Jessie
                          </p>
                          <span className="rounded-full bg-[#2070E5] px-3 py-1 text-white lg:text-[16px]">
                            油性肌
                          </span>
                        </div>

                        {/* 對比圖片 */}
                        <div className="relative mb-4">
                          <div className="grid grid-cols-2 gap-0">
                            <div className="relative h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/1-oily-before.png"
                                alt="使用前"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                剛上妝
                              </div>
                            </div>

                            <div className="relative -mx-1 h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/1-oily-after.png"
                                alt="使用後"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                10小時後
                              </div>
                            </div>
                          </div>

                          {/* 評分徽章 */}
                          <div
                            className="absolute -bottom-4 -right-4 z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#2070E5] text-[40px] font-bold text-white shadow-lg"
                            style={{ boxShadow: '0 0 0 4px white' }}
                          >
                            {round3[0].oily.rating}
                          </div>
                        </div>

                        <p className="mb-4 text-[20px] leading-relaxed text-gray-700">
                          {round3[0].oily.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block lg:pt-10">
                <div className="mx-auto max-w-[1082px] rounded-xl border border-gray-100 bg-white px-2 pb-2 pt-6 shadow-md lg:px-8 lg:pb-5">
                  {/* 桌面版 */}
                  <div className="">
                    <div className="px-4 py-2 md:px-8">
                      <p
                        className="text-center text-[24px] font-extrabold lg:text-[32px]"
                        style={{ color: '#2070E5' }}
                      >
                        油性肌之選：
                        <span className="font-bold">TIRTIR</span>
                      </p>
                    </div>
                    {/* 產品標題區 */}
                    <div className="mb-6 mt-6 flex items-center justify-center">
                      <img
                        src={getProductImageByID2(round3[1].id)}
                        alt={dataSources[round3[1].id - 1].product_name}
                        className="h-23 mr-4 w-20 object-contain"
                      />
                      <div className="text-left">
                        <p className="text-[32px] font-bold">
                          {dataSources[round3[1].id - 1].brand_name}
                        </p>
                        <p className="text-[20px] text-gray-600">
                          {dataSources[round3[1].id - 1].product_name}
                        </p>
                      </div>
                    </div>

                    {/* 兩欄評測區域 */}
                    <div className="flex">
                      {/* 左側評測 */}
                      <div className="flex-1 pr-4">
                        <div className="mb-4 flex items-center">
                          <p className="mr-3 font-rubik text-[32px] font-bold text-[#2070E5]">
                            Manyi
                          </p>
                          <span className="rounded-full bg-[#2070E5] px-3 py-1 text-white lg:text-[16px]">
                            乾性肌
                          </span>
                        </div>

                        {/* 對比圖片 */}
                        <div className="relative mb-4">
                          <div className="grid grid-cols-2 gap-0">
                            <div className="relative h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/2-dry-before.png"
                                alt="使用前"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                剛上妝
                              </div>
                            </div>

                            <div className="relative -mx-1 h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/2-dry-after.png"
                                alt="使用後"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                10小時後
                              </div>
                            </div>
                          </div>

                          {/* 評分徽章 */}
                          <div
                            className="absolute -bottom-4 -right-4 z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#2070E5] text-[40px] font-bold text-white shadow-lg"
                            style={{ boxShadow: '0 0 0 4px white' }}
                          >
                            {round3[1].dry.rating}
                          </div>
                        </div>

                        <p className="mb-4 text-[20px] leading-relaxed text-gray-700">
                          {round3[1].dry.desc}
                        </p>

                        {/* 額外圖片 */}
                        {round3[1].dry.extra && (
                          <div className="grid grid-cols-2 gap-0">
                            <img
                              src="/images/2024/cushion-review/round3/2-dry-extra.png"
                              alt="細節圖"
                              className="mb-2 h-[170px] w-[240px] object-cover"
                            />
                            <div className="flex items-center justify-center">
                              <p className="item-center mx-4 justify-center text-[20px] text-gray-600">
                                {round3[1].dry.extra}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 中間分隔線 */}
                      <div className="mx-4 w-px bg-gray-100"></div>

                      {/* 右側評測 */}
                      <div className="flex-1 pl-4">
                        <div className="mb-4 flex items-center">
                          <p className="mr-3 font-rubik text-[32px] font-bold text-[#2070E5]">
                            Jessie
                          </p>
                          <span className="rounded-full bg-[#2070E5] px-3 py-1 text-white lg:text-[16px]">
                            油性肌
                          </span>
                        </div>

                        {/* 對比圖片 */}
                        <div className="relative mb-4">
                          <div className="grid grid-cols-2 gap-0">
                            <div className="relative h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/2-oily-before.png"
                                alt="使用前"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                剛上妝
                              </div>
                            </div>

                            <div className="relative -mx-1 h-[350px] w-[240px]">
                              <img
                                src="/images/2024/cushion-review/round3/2-oily-after.png"
                                alt="使用後"
                                className="object-cover"
                              />
                              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                                10小時後
                              </div>
                            </div>
                          </div>

                          {/* 評分徽章 */}
                          <div
                            className="absolute -bottom-4 -right-4 z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#2070E5] text-[40px] font-bold text-white shadow-lg"
                            style={{ boxShadow: '0 0 0 4px white' }}
                          >
                            {round3[1].oily.rating}
                          </div>
                        </div>

                        <p className="mb-4 text-[20px] leading-relaxed text-gray-700">
                          {round3[1].oily.desc}
                        </p>

                        {/* 額外圖片 */}
                        {round3[1].oily.extra && (
                          <div className="grid grid-cols-2 gap-0">
                            <img
                              src="/images/2024/cushion-review/round3/2-oily-extra.png"
                              alt="細節圖"
                              className="mb-2 h-[170px] w-[240px] object-cover"
                            />
                            <div className="flex items-center justify-center">
                              <p className="item-center mx-4 justify-center text-[20px] text-gray-600">
                                {round3[1].oily.extra}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 手機版佈局 */}
                <div className="block lg:hidden">
                  <div className="px-4 py-2 md:px-20">
                    <p
                      className="text-center text-[20px] font-medium"
                      style={{ color: '#2070E5' }}
                    >
                      油性肌之選：
                      <span className="font-bold">HAUS LABS</span>
                    </p>
                  </div>
                  {/* 產品標題區 */}
                  <div className="mb-6 flex items-center justify-center pl-2">
                    <img
                      src={getProductImageByID2(round3[1].id)}
                      alt={dataSources[round3[1].id - 1].product_name}
                      className="mr-4 h-16 w-12 object-contain"
                    />
                    <div className="text-left">
                      <p className="text-[16px] font-bold">
                        {dataSources[round3[1].id - 1].brand_name}
                      </p>
                      <p className="text-[16px] text-gray-600">
                        {dataSources[round3[1].id - 1].product_name}
                      </p>
                    </div>
                  </div>
                  <div className="mb-2 flex flex-wrap items-center justify-center gap-1 lg:gap-4">
                    <div
                      className={`w-[140px] cursor-pointer rounded-full py-1 text-center font-rubik text-[16px] font-semibold lg:w-[240px] lg:px-3 lg:text-[24px] ${selectedOilyFirstFactor == 'dry' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                      onClick={() => setSelectedOilyFirstFactor('dry')}
                    >
                      <span>乾性肌</span>
                    </div>
                    <div
                      className={`w-[140px] cursor-pointer rounded-full py-1 text-center font-rubik text-[16px] font-semibold lg:w-[240px] lg:px-3 lg:text-[24px] ${selectedOilyFirstFactor == 'oily' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                      onClick={() => setSelectedOilyFirstFactor('oily')}
                    >
                      <span>油性肌</span>
                    </div>
                  </div>
                  <div className="items-top mx-0 flex flex-wrap justify-center px-0">
                    {round3.slice(1, 2).map((result, index) => (
                      <Round3MainCard
                        index={index + 2}
                        product={result}
                        factor={selectedOilyFirstFactor}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* 手機版佈局 */}
              <div className="mx-auto block max-w-[327px] rounded-xl border border-gray-100 bg-white pb-2 pt-6 shadow-md lg:hidden">
                <div className="px-4 py-2">
                  <p
                    className="text-center text-[20px] font-medium"
                    style={{ color: '#2070E5' }}
                  >
                    乾性肌之選：
                    <span className="font-bold">Estée Lauder</span>
                  </p>
                </div>
                {/* 產品標題區 */}
                <div className="justify-left mb-6 mt-2 flex items-center pl-4">
                  <img
                    src={getProductImageByID2(round3[0].id)}
                    alt={dataSources[round3[0].id - 1].product_name}
                    className="mr-4 h-16 w-auto object-contain"
                  />
                  <div className="text-left">
                    <p className="text-[16px] font-bold">
                      {dataSources[round3[0].id - 1].brand_name}
                    </p>
                    <p className="text-[16px] text-gray-600">
                      {dataSources[round3[0].id - 1].product_name}
                    </p>
                  </div>
                </div>
                <div className="mb-2 flex flex-wrap items-center justify-center gap-1 lg:gap-4">
                  <div
                    className={`w-[140px] cursor-pointer rounded-full py-1 text-center font-rubik text-[16px] font-semibold ${selectedDryFirstFactor == 'dry' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                    onClick={() => setSelectedDryFirstFactor('dry')}
                  >
                    <span>乾性肌</span>
                  </div>
                  <div
                    className={`w-[140px] cursor-pointer rounded-full py-1 text-center font-rubik text-[16px] font-semibold ${selectedDryFirstFactor == 'oily' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                    onClick={() => setSelectedDryFirstFactor('oily')}
                  >
                    <span>油性肌</span>
                  </div>
                </div>
                <div className="items-top mx-0 flex flex-wrap justify-center px-0">
                  {round3.slice(0, 1).map((result, index) => (
                    <Round3MobileMainCard
                      index={index + 1}
                      product={result}
                      factor={selectedDryFirstFactor}
                    />
                  ))}
                </div>
              </div>

              {/* 手機版佈局 */}
              <div className="block pt-6 lg:hidden">
                <div className="mx-auto max-w-[327px] rounded-xl border border-gray-100 bg-white pb-2 pt-6 shadow-md">
                  <div className="px-4 py-2 md:px-20">
                    <p
                      className="text-center text-[20px] font-medium"
                      style={{ color: '#2070E5' }}
                    >
                      油性肌之選：
                      <span className="font-bold">TIRTIR</span>
                    </p>
                  </div>
                  {/* 產品標題區 */}
                  <div className="mb-6 mt-2 flex items-center justify-center pl-4">
                    <img
                      src={getProductImageByID2(round3[1].id)}
                      alt={dataSources[round3[1].id - 1].product_name}
                      className="mr-4 h-16 w-auto object-contain"
                    />
                    <div className="text-left">
                      <p className="text-[16px] font-bold">
                        {dataSources[round3[1].id - 1].brand_name}
                      </p>
                      <p className="text-[16px] text-gray-600">
                        {dataSources[round3[1].id - 1].product_name}
                      </p>
                    </div>
                  </div>
                  <div className="mb-2 flex flex-wrap items-center justify-center gap-1 lg:gap-4">
                    <div
                      className={`w-[140px] cursor-pointer rounded-full py-1 text-center font-rubik text-[16px] font-semibold lg:w-[240px] lg:px-3 lg:text-[24px] ${selectedDryFirstFactor == 'dry' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                      onClick={() => setSelectedDryFirstFactor('dry')}
                    >
                      <span>乾性肌</span>
                    </div>
                    <div
                      className={`w-[140px] cursor-pointer rounded-full py-1 text-center font-rubik text-[16px] font-semibold lg:w-[240px] lg:px-3 lg:text-[24px] ${selectedDryFirstFactor == 'oily' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                      onClick={() => setSelectedDryFirstFactor('oily')}
                    >
                      <span>油性肌</span>
                    </div>
                  </div>
                  <div className="items-top mx-0 flex flex-wrap justify-center px-0">
                    {round3.slice(1, 2).map((result, index) => (
                      <Round3MobileMainCard
                        index={index + 2}
                        product={result}
                        factor={selectedDryFirstFactor}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <p
                className="mb-4 pt-8 text-center text-[20px] font-extrabold lg:text-[32px]"
                style={{ color: '#2070E5' }}
              >
                其它結果
              </p>

              <div className="mb-4 flex flex-wrap items-center justify-center gap-1 lg:gap-4">
                <div
                  className={`w-[160px] cursor-pointer rounded-full py-2 text-center font-rubik text-[16px] font-semibold lg:w-[240px] lg:px-3 lg:text-[24px] ${selectedFactor == 'dry' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                  onClick={() => setSelectedFactor('dry')}
                >
                  <span>Manyi 乾性肌</span>
                </div>
                <div
                  className={`w-[160px] cursor-pointer rounded-full py-2 text-center font-rubik text-[16px] font-semibold lg:w-[240px] lg:px-3 lg:text-[24px] ${selectedFactor == 'oily' ? 'bg-[#2070E5] text-white' : 'border border-blue-500 bg-white text-[#2070E5]'}`}
                  onClick={() => setSelectedFactor('oily')}
                >
                  <span>Jessie 油性肌</span>
                </div>
              </div>
              <div className="items-top flex hidden flex-wrap justify-center gap-x-8 gap-y-6 pb-4 pt-4 lg:flex">
                {round3.slice(2, 10).map((result, index) => (
                  <Round3Card
                    index={index + 3}
                    product={result}
                    factor={selectedFactor}
                    className={''}
                  />
                ))}
              </div>

              <div className="scrollbar-hide flex gap-1 overflow-x-auto pb-4 pt-4 lg:hidden">
                {round3.slice(2, 10).map((result, index) => (
                  <div className="h-full flex-shrink-0 border-r px-4">
                    <Round3Card
                      index={index + 3}
                      product={result}
                      factor={selectedFactor}
                      className={''}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Round4 區塊 */}
          <section id="round4"></section>
          <div className="mx-auto w-full max-w-[1440px] px-2 py-4">
            <div
              className="text-center text-[24px] font-bold lg:mb-4 lg:pt-12 lg:text-[32px]"
              style={{ color: '#1E70FF' }}
            >
              Round 4：氧化度測試
            </div>

            <div className="px-4 pt-2 md:px-20 lg:mb-10">
              <p className="text-center text-[16px] text-base leading-relaxed text-gray-700 lg:text-[20px]">
                將剛上妝以及靜置30分鐘後的粉底作比較，觀察顏色變化、氧化／暗沉問題。
              </p>
            </div>

            {/* 桌面版佈局 */}
            <div className="hidden lg:block">
              <div className="item-center flex justify-center lg:flex-wrap lg:gap-8">
                <div className="flex-1 text-center lg:max-w-[440px]">
                  <div className="mb-8">
                    <p className="text-[20px] font-bold text-[#2070E5] lg:text-[24px]">
                      最大色差：TIRTIR
                    </p>
                  </div>
                  <Round4Card
                    key={round4[0].id}
                    id={round4[0].id}
                    index={1}
                    round={4}
                    isEnlarge={true}
                  />
                  <div className="my-4">
                    <p className="text-center text-[16px] text-base leading-relaxed lg:text-[20px]">
                      {round4[0].desc}
                    </p>
                  </div>
                </div>
                <div className="flex-1 text-center lg:max-w-[440px]">
                  <div className="mb-8">
                    <p className="text-[20px] font-bold text-[#2070E5] lg:text-[24px]">
                      最少色差：Estée Lauder
                    </p>
                  </div>
                  <Round4Card
                    key={round4[1].id}
                    id={round4[1].id}
                    index={2}
                    round={4}
                    isEnlarge={true}
                  />
                  <div className="my-4">
                    <p className="text-center text-[16px] text-base leading-relaxed lg:text-[20px]">
                      {round4[1].desc}
                    </p>
                  </div>
                </div>
              </div>

              <p
                className="pt-10 text-center text-[24px] font-medium lg:text-[24px]"
                style={{ color: '#2070E5' }}
              >
                其它結果
              </p>
              <div className="item-center hidden flex-wrap justify-center gap-4 lg:flex lg:pb-10 lg:pt-4">
                {round4.slice(2, 7).map((result, index) => (
                  <div className="my-4 w-[calc(25%-1.5rem)] lg:min-w-[290px]">
                    <Round4Card
                      key={result.id}
                      id={result.id}
                      index={index + 3}
                      round={4}
                      isEnlarge={true}
                    />
                    <div className="my-2">
                      <p className="text-center text-[16px] text-base leading-relaxed lg:text-[20px]">
                        {result.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 手機版佈局 */}
            <div className="block lg:hidden">
              <div className="item-center mx-auto flex flex-col items-center justify-center px-2 pt-2">
                <div className="flex-1 text-center">
                  <div className="mb-8 mt-2">
                    <p className="text-[20px] font-bold text-[#2070E5] lg:text-[32px]">
                      最大色差：TIRTIR
                    </p>
                  </div>
                  <div className="mx-auto w-[327px] items-center justify-center">
                    <Round4Card
                      key={round4[0].id}
                      id={round4[0].id}
                      index={1}
                      round={4}
                      isEnlarge={true}
                    />
                  </div>
                  <div className="mt-2">
                    <p className="text-center text-[16px] text-base leading-relaxed lg:text-[20px]">
                      {round4[0].desc}
                    </p>
                  </div>
                </div>
                <div className="flex-1 text-center">
                  <div className="mb-8 mt-6">
                    <p className="text-[20px] font-bold text-[#2070E5] lg:text-[32px]">
                      最少色差：Estée Lauder
                    </p>
                  </div>
                  <div className="mx-auto w-[327px] items-center justify-center">
                    <Round4Card
                      key={round4[1].id}
                      id={round4[1].id}
                      index={2}
                      round={5}
                      isEnlarge={true}
                    />
                  </div>
                  <div className="mt-2">
                    <p className="text-center text-[20px] text-base leading-relaxed">
                      {round4[1].desc}
                    </p>
                  </div>
                </div>
              </div>
              <p
                className="pt-6 text-center text-[20px] font-bold"
                style={{ color: '#2070E5' }}
              >
                其它結果
              </p>
              <div className="-mx-4 flex gap-1 overflow-x-auto px-4 pb-4 pt-4 lg:hidden">
                {round4.slice(2, 6).map((result, index) => (
                  <div className="my-4 w-[180px] flex-shrink-0 px-2">
                    <Round4Card
                      key={result.id}
                      id={result.id}
                      index={index + 3}
                      round={4}
                      isEnlarge={false}
                    />
                    <div className="mt-1">
                      <p className="text-center text-[16px] text-base leading-relaxed lg:text-[20px]">
                        {result.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Round5 區塊 */}
          <section id="round5"></section>
          <div style={{ backgroundColor: '#FAFAFA' }}>
            <div className="mx-auto w-full max-w-[1440px] px-2 pt-8 lg:py-4">
              <div
                className="mb-6 text-center text-[24px] font-bold lg:text-[32px]"
                style={{ color: '#1E70FF' }}
              >
                Round 5：粉撲使用感
              </div>

              <div className="mb-6 px-4 md:px-20 lg:py-2">
                <p className="text-center text-[16px] text-base leading-relaxed text-gray-700 lg:text-[20px]">
                  分別以順手程度、厚薄、上面感覺來評價粉樸使用感～
                </p>
              </div>

              {/* 桌面版佈局 */}
              <div className="hidden lg:block">
                <div className="item-center flex flex-wrap justify-center gap-4">
                  {round5.slice(0, 3).map((result, index) => (
                    <div className="my-4 w-[calc(25%-1.5rem)] min-w-[390px]">
                      <Round5Card
                        key={result.id}
                        id={result.id}
                        index={index + 1}
                        round={5}
                        isEnlarge={true}
                        isMove={false}
                      />
                      <div className="mt-[10px]">
                        <p className="text-center text-base leading-relaxed lg:text-[20px]">
                          {result.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="item-center flex flex-wrap justify-center gap-4 py-8">
                  {round5.slice(3, 7).map((result, index) => (
                    <div className="w-[calc(25%-1.5rem)] min-w-[390px]">
                      <Round5Card
                        key={result.id}
                        id={result.id}
                        index={index + 4}
                        round={5}
                        isEnlarge={true}
                      />

                      <div className="mt-[10px]">
                        <p className="text-center text-base leading-relaxed lg:text-[20px]">
                          {result.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 手機版佈局 */}
              <div className="block lg:hidden">
                <div className="-mx-2 overflow-x-auto px-6 pb-4 pt-4">
                  <div
                    className="grid gap-6"
                    style={{
                      gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))',
                      gridTemplateRows: 'repeat(2, 1fr)',
                      width: 'max-content',
                      minWidth: '100%',
                    }}
                  >
                    {round5.slice(0, 6).map((result, index) => (
                      <div className="w-[270px]">
                        <Round5Card
                          key={result.id}
                          id={result.id}
                          index={index + 1}
                          round={4}
                          isEnlarge={false}
                          isMove={false}
                          style={{
                            gridColumn: Math.floor(index / 2) + 1,
                            gridRow: (index % 2) + 1,
                          }}
                        />
                        <div className="mt-2">
                          <p className="pb-6 text-center text-[16px] text-base leading-relaxed">
                            {result.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Round 6 綜合分析卡片區塊 */}
          <section id="round5"></section>
          <div className="mx-auto w-full max-w-[1440px] px-2 pt-8 lg:py-4">
            <div className="mx-auto max-w-7xl lg:p-6">
              <div
                className="text-center text-[24px] font-bold lg:mt-4 lg:text-[32px]"
                style={{ color: '#2070E5' }}
              >
                綜合實測結果
              </div>

              <div className="px-6 pb-2 pt-4 lg:mb-8 lg:px-0">
                <p className="text-center text-[16px] leading-relaxed lg:text-[20px]">
                  6款氣墊粉底中，乾／油肌喜好各有不同。乾肌編輯 Manyi 認為 Estée
                  Lauder
                  整體維持度為眾多選擇中最好，在持久度高同時不會抽乾皮膚水份，而且妝感在皮膚出油後變成奶油肌！油肌編輯
                  Jessie 則認為 TIRTIR 持久度最好，感覺更加與 YSL 相似！其中
                  TIRTIR 遮瑕度更高，所以妝感較為明顯～
                </p>
              </div>
              <Round6Table products={round6} size={6}>
                {' '}
              </Round6Table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MoistureOilBar = ({ data, index, dataSources, className = '' }) => {
  const waterChange = data.water_count;
  const oilChange = data.oil_count;
  const netChange = waterChange - oilChange; // 正值=偏保濕，負值=偏控油
  const maxChange = 20; // 假設最大變化量為 ±20
  const normalizedChange = Math.max(-1, Math.min(1, netChange / maxChange));

  // 轉換為位置（0-100）
  const posWithOffset = 50 - normalizedChange * 40;

  return (
    <div
      className={`flex max-h-[352px] w-full max-w-[327px] flex-col items-center rounded-xl border-b bg-white p-4 shadow-md lg:max-h-[406px] lg:max-w-[592px] lg:p-6 ${className}`}
    >
      <div className="flex w-full flex-row">
        <div className="mr-4 flex flex-col items-center lg:mr-8">
          <img
            src={`/images/2024/cushion-review/products/${data.id}.png`}
            className="mb-2 h-15 w-15 lg:h-32 lg:w-32"
          />
        </div>
        <div className={`w-full flex-1`}>
          <p className="pb-4 font-rubik text-[24px] font-bold">
            {dataSources[data.id - 1].brand_name}
          </p>
          <div className="relative mb-8 flex w-[219px] items-center lg:w-[360px]">
            {/* 黑線 */}
            <div className="h-[4px] w-full bg-black"></div>

            {/* 左側箭頭 */}
            <img
              src="/svgs/hollow-arrow-left.svg"
              className="absolute left-1 z-10 -translate-x-1/2"
            />

            <img
              src="/svgs/hollow-arrow-right.svg"
              className="absolute -right-[12px] z-10 -translate-x-1/2"
            />

            {/* 藍色標記鑽石 */}
            <img
              src="/images/diamond-blue.png"
              className={`absolute z-10 h-[23px] w-[16px] -translate-x-1/2 lg:h-[36px] lg:w-[25px]`}
              style={{ left: `${posWithOffset}%` }}
            />
            <span className="absolute -bottom-8 left-0 text-[14px] text-[#221F20] lg:-bottom-12 lg:text-[20px]">
              保濕
            </span>
            <span className="absolute -bottom-8 -right-1 text-[14px] text-[#221F20] lg:-bottom-12 lg:text-[20px]">
              控油
            </span>
          </div>
        </div>
      </div>

      <div className="justify-left flex w-full items-center space-x-5 pb-4 lg:space-x-6">
        <span className="justify-left whitespace-nowrap rounded-3xl bg-[#1E70FF] px-2 py-0.5 text-[14px] text-white lg:px-4 lg:py-2 lg:text-[16px]">
          上妝後
        </span>
        <div className="flex items-center space-x-1 lg:space-x-2">
          <span className="rounded-3xl bg-black px-2 py-1 text-[16px] text-white lg:px-3 lg:py-2 lg:text-[20px]">
            水
          </span>
          <p className="font-rubik text-[20px] font-bold lg:text-[40px]">
            {data.water}
          </p>
          <p
            className={`font-rubik text-[16px] font-bold lg:text-[24px] ${data.water_count < 0 ? 'text-[#F16B7C]' : 'text-[#1E70FF]'}`}
          >
            ({data.water_count})
          </p>
        </div>
        <div className="flex items-center space-x-1 lg:space-x-2">
          <span className="rounded-3xl bg-black px-2 py-1 text-[16px] text-white lg:px-3 lg:py-2 lg:text-[20px]">
            油
          </span>
          <p className="font-rubik text-[20px] font-bold lg:text-[40px]">
            {data.oil}
          </p>
          <p
            className={`font-rubik text-[16px] font-bold lg:text-[24px] ${data.oil_count < 0 ? 'text-[#F16B7C]' : 'text-[#1E70FF]'}`}
          >
            ({data.oil_count})
          </p>
        </div>
      </div>
      <div className="h-[2px] w-full bg-black"></div>
      <div className="justify-left mb-0 flex w-full flex-1 flex-col items-center pt-4">
        <div className="flex w-full items-center">
          <p className="mr-3 font-rubik text-[24px] font-bold text-[#1E70FF] lg:text-[32px]">
            {skinTypeMap[data.type].reviewer}
          </p>
          <span className="whitespace-nowrap rounded-full bg-[#1E70FF] px-4 py-1.5 text-[16px] text-white">
            {skinTypeMap[data.type].label}
          </span>
        </div>
        <div className="justify-left flex w-full items-center space-x-6 text-[16px] lg:pt-4 lg:text-[20px]">
          <p>{data.desc}</p>
        </div>
      </div>
    </div>
  );
};

const MinMoistureOilBar = ({ data, index, dataSources, className = '' }) => {
  const waterChange = data.water_count;
  const oilChange = data.oil_count == '-' ? 0 : data.oil_count;
  const netChange = waterChange - oilChange; // 正值=偏保濕，負值=偏控油
  const maxChange = 20; // 假設最大變化量為 ±20
  const normalizedChange = Math.max(-1, Math.min(1, netChange / maxChange));

  // 轉換為位置（0-100）
  const posWithOffset = 50 - normalizedChange * 40;
  return (
    <div
      className={`flex h-[340px] w-full max-w-[306px] flex-col items-center rounded-xl border-b bg-white p-4 shadow-md lg:h-[311px] lg:max-w-[306px] ${className}`}
    >
      <div className="flex w-full flex-row">
        <div className="mr-4 flex flex-col items-center">
          <img
            src={`/images/2024/cushion-review/products/${data.id}.png`}
            className="h-12 w-auto lg:h-15 lg:w-auto"
          />
        </div>
        <div className={`w-full flex-1`}>
          <p className="pb-2 font-rubik text-[24px] font-bold">
            {dataSources[data.id - 1].brand_name}
          </p>
          <div className="relative mb-4 flex w-[198px] items-center">
            {/* 黑線 */}
            <div className="h-[4px] w-full bg-black"></div>

            {/* 左側箭頭 */}
            <img
              src="/svgs/hollow-arrow-left.svg"
              className="absolute left-1 z-10 -translate-x-1/2"
            />

            <img
              src="/svgs/hollow-arrow-right.svg"
              className="absolute -right-[12px] z-10 -translate-x-1/2"
            />

            {/* 藍色標記鑽石 */}
            <img
              src="/images/diamond-blue.png"
              className="absolute z-10 h-[23px] w-[16px] -translate-x-1/2 lg:h-[23px] lg:w-[16px]"
              style={{ left: `${posWithOffset}%` }}
            />
            <span className="absolute -bottom-8 left-0 text-[14px] text-[#221F20]">
              保濕
            </span>
            <span className="absolute -bottom-8 -right-1 text-[14px] text-[#221F20]">
              控油
            </span>
          </div>
        </div>
      </div>

      <div className="justify-left flex w-full items-center space-x-3 pb-4 pt-8">
        <span className="justify-left whitespace-nowrap rounded-3xl bg-[#1E70FF] px-2 py-1 text-[14px] text-white">
          上妝後
        </span>
        <div className="flex items-center space-x-1">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[16px] text-white">
            水
          </span>
          <p className="font-rubik text-[20px] font-bold">{data.water}</p>
          <p
            className={`font-rubik text-[16px] font-bold ${data.water_count < 0 ? 'text-[#F16B7C]' : 'text-[#1E70FF]'}`}
          >
            ({data.water_count})
          </p>
        </div>
        <div className="flex items-center space-x-1">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[16px] text-white">
            油
          </span>
          <p className="font-rubik text-[20px] font-bold">{data.oil}</p>
          <p
            className={`font-rubik text-[16px] font-bold ${data.oil_count < 0 ? 'text-[#F16B7C]' : 'text-[#1E70FF]'}`}
          >
            ({data.oil_count})
          </p>
        </div>
      </div>
      <div className="h-[2px] w-full bg-black lg:my-0.5"></div>
      <div className="justify-left mb-0 flex w-full flex-1 flex-col items-center pt-4">
        <div className="flex w-full items-center">
          <p className="mr-3 font-rubik text-[24px] font-bold text-[#1E70FF] lg:text-[20px]">
            {skinTypeMap[data.type].reviewer}
          </p>
          <span className="whitespace-nowrap rounded-full bg-[#1E70FF] px-4 py-1 text-[16px] text-white lg:text-[14px]">
            {skinTypeMap[data.type].label}
          </span>
        </div>
        <div className="justify-left flex w-full items-center space-x-6 text-[16px]">
          <p>{data.desc}</p>
        </div>
      </div>
    </div>
  );
};

const Round2Card = ({
  id,
  index,
  rating,
  round,
  desc,
  isEnlarge = false,
  isMove = true,
}) => {
  const product = dataSources[id - 1];
  const productImage = `/images/2024/cushion-review/products/${product.id}.png`;
  const resultImage = `/images/2024/cushion-review/round2/${index}.png`;

  return (
    <>
      <div className="relative w-full rounded-lg">
        <div
          className={`absolute -top-6 left-1/2 inline-flex min-w-max -translate-x-1/2 transform items-center whitespace-nowrap rounded-full px-2 py-1 text-center lg:left-1/2 lg:-translate-x-1/2 lg:px-6 lg:py-2`}
          style={{ backgroundColor: '#2070E5' }}
        >
          {/* 產品圖 */}
          <img
            src={productImage}
            className="mr-2 lg:h-7 lg:w-auto"
            alt={index + 1}
          />
          <span
            className={`text-white lg:text-[20px] ${isEnlarge ? 'text-[20px]' : round == 1 ? 'text-[12px]' : 'text-[14px]'}`}
          >
            {product.brand_name}
          </span>
        </div>
        {/* 背景結果圖 */}
        <img
          src={resultImage}
          alt="測試結果"
          className={`${round == 4 ? 'rounded-3xl border-2 border-blue-500 lg:border-4' : ''} object-cover lg:h-full lg:w-full ${isEnlarge ? 'h-full w-full' : round == 1 || round == 2 ? 'h-[220px]' : 'h-full w-full'} `}
        />
      </div>
    </>
  );
};

const Round2MobileCard = ({
  id,
  index,
  rating,
  round,
  desc,
  isEnlarge = false,
  isMove = true,
}) => {
  const product = dataSources[id - 1];
  const productImage = `/images/2024/cushion-review/products/${product.id}.png`;
  const resultImage = `/images/2024/cushion-review/round2/${index}.png`;

  return (
    <>
      <div className="relative w-full rounded-lg">
        <div
          className={`absolute -top-4 left-1/2 inline-flex min-w-max -translate-x-1/2 transform items-center whitespace-nowrap rounded-full ${isEnlarge ? 'px-4 py-2' : 'px-2 py-1'} text-center`}
          style={{ backgroundColor: '#2070E5' }}
        >
          {/* 產品圖 */}
          <img
            src={productImage}
            className={`mr-2 ${isEnlarge ? 'h-7' : 'h-4'} w-auto`}
            alt={index + 1}
          />
          <span
            className={`text-white ${isEnlarge ? 'text-[16px]' : 'text-[14px]'}`}
          >
            {product.brand_name}
          </span>
        </div>
        {/* 背景結果圖 */}
        <img
          src={resultImage}
          alt="測試結果"
          className={`lg:h-full lg:w-full ${isEnlarge ? 'h-full w-full' : 'h-[215px] w-[148px]'} `}
        />
      </div>
    </>
  );
};

const Round3MainCard = ({}) => {
  return (
    <div className="flex">
      {/* 左側評測 */}
      <div className="flex-1 pr-4">
        <div className="mb-4 flex items-center">
          <p className="mr-3 text-xl font-bold text-[#2070E5]">Manyi</p>
          <span className="rounded-full bg-[#2070E5] px-3 py-1 text-white lg:text-[16px]">
            乾性肌
          </span>
        </div>

        {/* 對比圖片 */}
        <div className="relative mb-4">
          <div className="grid grid-cols-2 gap-0">
            <div className="relative h-[350px] w-[240px]">
              <img
                src="/images/2024/cushion-review/round3/1-dry-before.png"
                alt="使用前"
                className="object-cover"
              />
              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                剛上妝
              </div>
            </div>

            <div className="relative -mx-1 h-[350px] w-[240px]">
              <img
                src="/images/2024/cushion-review/round3/1-dry-after.png"
                alt="使用後"
                className="object-cover"
              />
              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                10小時後
              </div>
            </div>
          </div>

          {/* 評分徽章 */}
          <div
            className="absolute -bottom-4 -right-4 z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#2070E5] text-[40px] font-bold text-white shadow-lg"
            style={{ boxShadow: '0 0 0 4px white' }}
          >
            {round3[0].dry.rating}
          </div>
        </div>

        <p className="mb-4 text-[20px] leading-relaxed text-gray-700">
          {round3[0].dry.desc}
        </p>

        {/* 額外圖片 */}
        {round3[0].dry.extra && (
          <div className="grid grid-cols-2 gap-0">
            <img
              src="/images/2024/cushion-review/round3/1-dry-extra.png"
              alt="細節圖"
              className="mb-2 h-[170px] w-[240px] object-cover"
            />
            <div className="flex items-center justify-center">
              <p className="item-center mx-4 justify-center text-[20px] text-gray-600">
                {round3[0].dry.extra.desc}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 中間分隔線 */}
      <div className="mx-4 w-px bg-gray-100"></div>

      {/* 右側評測 */}
      <div className="flex-1 pl-4">
        <div className="mb-4 flex items-center">
          <p className="mr-3 text-xl font-bold text-[#2070E5]">Jessie</p>
          <span className="rounded-full bg-[#2070E5] px-3 py-1 text-white lg:text-[16px]">
            油性肌
          </span>
        </div>

        {/* 對比圖片 */}
        <div className="relative mb-4">
          <div className="grid grid-cols-2 gap-0">
            <div className="relative h-[350px] w-[240px]">
              <img
                src="/images/2024/cushion-review/round3/1-oily-before.png"
                alt="使用前"
                className="object-cover"
              />
              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                剛上妝
              </div>
            </div>

            <div className="relative -mx-1 h-[350px] w-[240px]">
              <img
                src="/images/2024/cushion-review/round3/1-oily-after.png"
                alt="使用後"
                className="object-cover"
              />
              <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-white lg:text-[16px]">
                10小時後
              </div>
            </div>
          </div>

          {/* 評分徽章 */}
          <div
            className="absolute -bottom-4 -right-4 z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#2070E5] text-[40px] font-bold text-white shadow-lg"
            style={{ boxShadow: '0 0 0 4px white' }}
          >
            {round3[0].oily.rating}
          </div>
        </div>

        <p className="mb-4 text-[20px] leading-relaxed text-gray-700">
          {round3[0].oily.desc}
        </p>
      </div>
    </div>
  );
};

const Round3Card = ({ index, product, factor, className = 'bg-white' }) => {
  return (
    <>
      <div
        className={`flex w-[240px] items-start ${className} py-2 lg:h-[380px] lg:w-[270px] lg:px-0`}
        key={`round3-${index}`}
      >
        {/* 桌面版 */}
        <div className="">
          {/* 產品標題區 */}
          <div className="justify-left mb-6 flex h-[70px] items-start">
            <img
              src={getProductImageByID2(product.id)}
              alt={dataSources[product.id - 1].product_name}
              className="mr-4 h-[60px] w-auto lg:h-[68px] lg:w-auto"
            />
            <div className="text-left">
              <p className="font-rubik text-[16px] font-bold">
                {dataSources[product.id - 1].brand_name}
              </p>
              <p className="whitespace-pre-line font-rubik text-[16px] text-gray-600">
                {dataSources[product.id - 1].product_name}
              </p>
            </div>
          </div>

          {/* 左側評測 */}
          <div className="flex flex-col items-start justify-center">
            {/* 對比圖片 */}
            <div className="relative mb-4">
              <div className="grid grid-cols-2 gap-0">
                <div className="relative h-[170px] w-[120px] lg:h-[190px] lg:w-[135px]">
                  <img
                    src={`/images/2024/cushion-review/round3/${index}-${factor}-before.png`}
                    alt="使用前"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-xs text-white">
                    剛上妝
                  </div>
                </div>

                <div className="relative h-[170px] w-[120px] lg:h-[190px] lg:w-[135px]">
                  <img
                    src={`/images/2024/cushion-review/round3/${index}-${factor}-after.png`}
                    alt="使用後"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-xs text-white">
                    10小時後
                  </div>
                </div>
              </div>

              {/* 評分徽章 */}
              <div
                className="absolute -bottom-4 -right-4 z-10 flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[#2070E5] text-[16px] font-bold text-white shadow-lg"
                style={{ boxShadow: '0 0 0 2px white' }}
              >
                {product[factor].rating}
              </div>
            </div>
            <div className="text-center">
              <p className="mb-4 text-[16px] leading-relaxed text-gray-700">
                {product[factor].desc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Round3MobileMainCard = ({ index, product, factor }) => {
  return (
    <>
      <div
        className="item-center w-[295px] bg-white px-0 py-2 lg:h-[270px] lg:px-0"
        key={`round3-${index}`}
      >
        {/* 桌面版 */}
        <div className="">
          {/* 產品標題區 */}
          <div className="items-top flex justify-center pb-2">
            <p
              className="text-center text-[20px] font-bold"
              style={{ color: '#2070E5' }}
            >
              {factor == 'oily' ? 'Jessie' : 'Manyi'}
            </p>
          </div>

          {/* 左側評測 */}
          <div className="item-center flex-col justify-center">
            {/* 對比圖片 */}
            <div className="relative mb-4">
              <div className="grid grid-cols-2 gap-0">
                <div className="relative h-[210px] w-[147.5px] lg:h-[190px] lg:w-[135px]">
                  <img
                    src={`/images/2024/cushion-review/round3/${index}-${factor}-before.png`}
                    alt="使用前"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-[16px] text-white">
                    剛上妝
                  </div>
                </div>

                <div className="relative h-[210px] w-[147.5px] lg:h-[190px] lg:w-[135px]">
                  <img
                    src={`/images/2024/cushion-review/round3/${index}-${factor}-after.png`}
                    alt="使用後"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute left-0 top-0 bg-[#2070E5] px-2 py-1 text-[16px] text-white">
                    10小時後
                  </div>
                </div>
              </div>

              {/* 評分徽章 */}
              <div
                className="absolute -bottom-4 -right-4 z-10 flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[#2070E5] text-[16px] font-bold text-white shadow-lg"
                style={{ boxShadow: '0 0 0 2px white' }}
              >
                {product[factor].rating}
              </div>
            </div>
            <div className="text-center">
              <p className="mb-4 text-[16px] leading-relaxed text-gray-700">
                {product[factor].desc}
              </p>
            </div>
            {/* 額外圖片 */}
            {product[factor].extra && (
              <div className="grid grid-cols-2 gap-0">
                <img
                  src={`/images/2024/cushion-review/round3/${index}-${factor}-extra.png`}
                  alt="細節圖"
                  className="mb-2 h-[91px] w-[128px] object-cover"
                />
                <div className="flex items-start justify-center">
                  <p className="item-center mx-2 justify-center text-center text-[16px] text-gray-600">
                    {product[factor].extra}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Round4Card = ({
  id,
  index,
  rating,
  round,
  desc,
  isEnlarge = false,
  isMove = true,
}) => {
  const product = dataSources[id - 1];
  const productImage = `/images/2024/cushion-review/products/${product.id}.png`;
  const resultImage = `/images/2024/cushion-review/round4/${index}.png`;

  return (
    <>
      <div className="relative w-full rounded-lg">
        <div
          className={`absolute left-1/2 inline-flex min-w-max -translate-x-1/2 transform items-center whitespace-nowrap rounded-full px-4 py-1 text-center lg:left-1/2 lg:-translate-x-1/2 lg:px-6 lg:py-2 ${round == 3 || round == 4 ? '-top-4' : isMove ? '-top-6' : 'top-0'}`}
          style={{ backgroundColor: '#2070E5' }}
        >
          {/* 產品圖 */}
          <img
            src={productImage}
            className={`mr-2 max-h-7 w-auto lg:h-7 ${isEnlarge ? 'h-7' : 'h-4'}`}
            alt={index + 1}
          />
          <span
            className={`font-rubik font-semibold text-white lg:text-[20px] ${isEnlarge ? 'text-[20px]' : 'text-[14px]'}`}
          >
            {product.brand_name}
          </span>
        </div>
        {/* 背景結果圖 */}
        <img
          src={resultImage}
          alt="測試結果"
          className={`${round == 4 ? 'rounded-xl border-2 border-white lg:border-4' : ''} object-cover lg:h-full lg:w-full ${isEnlarge ? 'h-[194px]' : 'h-[108px]'} `}
        />
      </div>
    </>
  );
};

const Round5Card = ({
  id,
  index,
  rating,
  round,
  desc,
  isEnlarge = false,
  isMove = true,
}) => {
  const product = dataSources[id - 1];
  const productImage = `/images/2024/cushion-review/products/${product.id}.png`;
  const resultImage = `/images/2024/cushion-review/round5/${index}.png`;

  return (
    <>
      <div className="relative w-full rounded-lg">
        <div
          className={`absolute left-1/2 inline-flex min-w-max -translate-x-1/2 transform items-center whitespace-nowrap rounded-full px-2 py-1 text-center lg:left-1/2 lg:-translate-x-1/2 lg:px-6 lg:py-2 ${isMove ? '-top-6' : '-top-4'}`}
          style={{ backgroundColor: '#2070E5' }}
        >
          {/* 產品圖 */}
          <img
            src={productImage}
            className="mr-2 h-7 max-h-7 w-auto"
            alt={index + 1}
            style={{
              height: `${isEnlarge ? '75%' : '70%'}`,
            }}
          />
          <span
            className={`font-bold text-white lg:text-[20px] ${isEnlarge ? 'text-[20px]' : 'text-[16px]'}`}
          >
            {product.brand_name}
          </span>
        </div>
        {/* 背景結果圖 */}
        <img
          src={resultImage}
          alt="測試結果"
          className={`rounded-xl border-4 border-blue-500 object-cover lg:h-full lg:w-full lg:border-4 ${isEnlarge ? 'h-full w-full' : ''} `}
        />
      </div>
    </>
  );
};

const getProductImageByID2 = (id) => {
  if (!id) return '';
  return `/images/2024/cushion-review/products/${id}.png`;
};

const Round6Table = ({ products = [], size = 8 }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const rows = [
    { key: 'origin', label: '產地' },
    { key: 'spf', label: '防曬度' },
    { key: 'price', label: '價錢/容量' },
    { key: 'description', label: '綜合使用感' },
    { key: 'rating', label: '綜合評分' },
  ];

  return (
    <div
      className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-gray-50 mx-0 w-full overflow-x-auto"
      key="round5_table"
    >
      {/* 表格容器 */}
      <div className="min-w-[1400px] bg-white p-4">
        {/* 產品頭部區域 */}
        <div
          className={`grid grid-cols-${size} h-[220px] lg:h-[356px]`}
          style={{
            gridTemplateColumns:
              windowWidth >= 1024
                ? `190px repeat(${size}, 315px)`
                : `108px repeat(${size}, 192px)`,
          }}
        >
          {/* 空白角落 - 無邊框 */}
          <div className="w-[108px] rounded-tl-lg lg:w-[190px]"></div>

          {/* 產品列 */}
          {products.map((product, index) => (
            <div
              key={product.id}
              className={`flex flex-col items-center justify-start rounded-t-lg border-l-2 border-t-2 border-blue-500 bg-[#F8F9FA] py-4 text-center lg:border-l-4 lg:border-t-4 lg:p-4 ${
                index === products.length - 1
                  ? 'rounded-tr-lg border-r-2 lg:border-r-4'
                  : ''
              }`}
            >
              <div className="flex h-[130px] flex-col justify-end lg:h-[250px]">
                {/* 推薦標籤 */}
                {product.label &&
                  product.label.map((label, index) => {
                    if (label.key != 'product') return null;
                    return (
                      <div
                        key={index}
                        className="mb-2 text-[14px] font-bold tracking-wide text-[#1E70FF] lg:my-auto lg:text-[20px]"
                      >
                        \ {label.desc} /
                      </div>
                    );
                  })}

                {/* 產品圖片 */}
                <div className="mb-3">
                  <img
                    src={getProductImageByID(product.id)}
                    alt={dataSources[product.id - 1].product_name}
                    className="mx-auto flex h-[95px] justify-end object-contain lg:h-[190px]"
                  />
                </div>
              </div>
              {/* 品牌名稱 */}
              <div className="mb-1 font-rubik text-[16px] font-bold font-semibold text-[#1E70FF] lg:text-[24px]">
                {dataSources[product.id - 1].brand_name}
              </div>

              {/* 產品名稱 */}
              <div className="lg:font-semiBold whitespace-pre-line text-[14px] font-bold leading-tight lg:hidden lg:text-[20px]">
                {dataSources[product.id - 1].product_name}
              </div>
              {/* 產品名稱 */}
              <div className="lg:font-semiBold hidden text-[14px] font-bold leading-tight lg:block lg:text-[20px]">
                {dataSources[product.id - 1].product_name}
              </div>
            </div>
          ))}
        </div>

        {/* 表格主體 */}
        {rows.map((row, rowIndex) => (
          <div
            key={row.key}
            className={`grid grid-cols-${size} `}
            style={{
              gridTemplateColumns:
                windowWidth >= 1024
                  ? `190px repeat(${size}, 315px)`
                  : `108px repeat(${size}, 192px)`,
            }}
          >
            {/* 行標籤 */}
            <div
              className={`flex w-[108px] items-center justify-center rounded-l-xl border-b-2 border-t-2 border-white bg-[#1E70FF] p-1 text-[16px] font-medium text-white lg:w-[190px] lg:rounded-l-2xl lg:border-l-4 lg:border-t-4 lg:p-3 lg:text-[24px] ${
                rowIndex === rows.length - 1
                  ? 'rounded-bl-xl lg:rounded-bl-2xl'
                  : ''
              }`}
              style={{
                height: (() => {
                  switch (row.key) {
                    case 'origin':
                    case 'spf':
                      return windowWidth >= 1024 ? '68px' : '36px';
                    case 'price':
                      return windowWidth >= 1024 ? '74px' : '57px';
                    case 'description':
                      return windowWidth >= 1024 ? '196px' : '169px';
                    case 'rating':
                      return windowWidth >= 1024 ? '64px' : '36px';
                    default:
                      return 'auto';
                  }
                })(),
              }}
            >
              {row.label}
            </div>

            {/* 產品數據列 */}
            {products.map((product, colIndex) => (
              <div
                key={`${row.key}-${product.id}`}
                className={`relative flex items-center justify-center border-l-2 border-t-2 border-blue-500 bg-[#F8F9FA] p-1 text-center text-[20px] lg:border-l-4 lg:border-t-4 lg:p-3 ${
                  rowIndex === rows.length - 1 ? 'border-b-2 lg:border-b-4' : ''
                } ${
                  colIndex === products.length - 1
                    ? 'border-r-2 lg:border-r-4'
                    : ''
                } ${rowIndex === rows.length - 1 && colIndex === products.length - 1 ? 'rounded-bl-rg' : ''}`}
                style={{
                  height: (() => {
                    switch (row.key) {
                      case 'origin':
                      case 'spf':
                        return windowWidth >= 1024 ? '68px' : '36px';
                      case 'price':
                        return windowWidth >= 1024 ? '74px' : '57px';
                      case 'description':
                        return windowWidth >= 1024 ? '196px' : '169px';
                      case 'rating':
                        return windowWidth >= 1024 ? '68px' : '36px';
                      default:
                        return 'auto';
                    }
                  })(),
                }}
              >
                {row.key === 'rating' ? (
                  <div
                    className={`font-rubik text-[24px] font-bold text-[#1E70FF] lg:text-[32px]`}
                  >
                    {product.rating}
                  </div>
                ) : row.key === 'price' ? (
                  <div>
                    {product.label &&
                      product.label.map((label, index) => {
                        if (label.key != 'price') return null;
                        return (
                          <div className="absolute -right-4 -top-8 z-10 lg:-right-8 lg:-top-10">
                            <div class="relative inline-block">
                              <img
                                src="/images/2024/cushion-review/bubble.png"
                                className="w-16 object-contain lg:h-[73px] lg:w-[104px]"
                              />
                              <div className="absolute inset-0 flex -translate-y-1 rotate-6 transform items-center justify-center text-[12px] font-bold text-[#1E70FF] lg:-translate-y-1 lg:text-[16px]">
                                {label.desc}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    <div className="text-[14px] lg:text-[20px]">
                      {dataSources[product.id - 1].price}
                    </div>
                    <div className="mt-1 text-[12px] text-gray-800 lg:text-[14px]">
                      平均HK${dataSources[product.id - 1].unit_price}/ml
                    </div>
                  </div>
                ) : row.key === 'spf' ? (
                  <div className="mx-2 my-4 text-left text-center text-[14px] leading-relaxed lg:mx-0 lg:my-8 lg:text-[20px]">
                    {product.label &&
                      product.label.map((label, index) => {
                        if (label.key != 'spf') return null;
                        return (
                          <div className="absolute -right-4 -top-8 z-10 lg:-right-8 lg:-top-10">
                            <div class="relative inline-block">
                              <img
                                src="/images/2024/cushion-review/bubble.png"
                                className="w-16 object-contain lg:h-[73px] lg:w-[104px]"
                              />
                              <div className="absolute inset-0 flex -translate-y-1 rotate-6 transform items-center justify-center text-[12px] font-bold text-[#1E70FF] lg:-translate-y-1 lg:text-[16px]">
                                {label.desc}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {product.spf}
                  </div>
                ) : row.key === 'description' ? (
                  <div className="mx-2 my-4 text-left text-center text-[14px] leading-relaxed lg:mx-0 lg:my-8 lg:text-[20px]">
                    {product.label &&
                      product.label.map((label, index) => {
                        if (label.key != 'description') return null;
                        return (
                          <div className="absolute -right-4 -top-8 z-10 lg:-right-8 lg:-top-10">
                            <div class="relative inline-block">
                              <img
                                src="/images/2024/cushion-review/bubble.png"
                                className="w-16 object-contain lg:h-[73px] lg:w-[104px]"
                              />
                              <div className="absolute inset-0 flex -translate-y-1 rotate-6 transform items-center justify-center text-[12px] font-bold text-[#1E70FF] lg:-translate-y-1 lg:text-[16px]">
                                {label.desc}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {product.overall_experience}
                  </div>
                ) : row.key === 'origin' ? (
                  <div className="text-left text-[14px] leading-relaxed lg:text-[20px]">
                    {dataSources[product.id - 1].origin}
                  </div>
                ) : (
                  <div>{product[row.key]}</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const getProductImageByID = (id) => {
  if (!id) return '';
  return `/images/2024/cushion-review/products/${id}.png`;
};

export async function getServerSideProps(ctx) {
  const slug = 'cushion-review-2024';
  try {
    const { data: premiumContentSeo } = await api.request({
      method: 'GET',
      url: `/api/seo/premiumContent/${slug}`,
    });
    const {
      page_title: pageTitle = '',
      page_meta_description: pageDescription = '',
    } = premiumContentSeo;

    const title = pageTitle || '';
    const description = pageDescription || '';

    return {
      props: {
        seo: { title, description },
      },
    };
  } catch (e) {
    // console.log(e);
  }
  return {
    props: {
      seo: {},
    },
  };
}

export default SunscreenResult;
